import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Tooltip,
  Box,
} from '@mui/material';
import { Edit, Check, Close, DoDisturbAlt } from '@mui/icons-material';
import { showErrorSnackbar } from '../../../components/snackBar/snakbar';
import { Recipient } from '../hooks/useSendEmail';

export interface EmailDetailsProps {
  recipients: Recipient[];
  onUpdateEmail: (id: number, newEmail: string) => void;
  setIsEditRecipient?: (id: number | null) => void;
  removeRecipient: (id: number) => void;
  addVoucherCol: boolean;
}

const actionBtnSx = {
  backgroundColor: 'white',
  borderRadius: '50%',
  p: '4px',
  '& .MuiSvgIcon-root': { fontSize: 18 },
};

const disabledActionBtnSx = {
  ...actionBtnSx,
  '&.Mui-disabled': {
    color: 'error.main',
    backgroundColor: 'white',
    opacity: 1,
    cursor: 'not-allowed',
  },
};

const actionCellSx = {
  backgroundColor: 'grey.300',
};

export const EmailDetails: React.FC<EmailDetailsProps> = ({
  recipients = [],
  onUpdateEmail,
  setIsEditRecipient,
  removeRecipient,
  addVoucherCol,
}) => {
  const [editingId, setEditingId] = useState<number | null>(null);
  const [emailValue, setEmailValue] = useState<string>('');

  const firstIndexOfId = (id: number) => recipients.findIndex((r) => r.recipientId === id);

  const handleEdit = (id: number, currentEmail: string) => {
    setEditingId(id);
    setEmailValue(currentEmail || '');
    if (setIsEditRecipient) {
      setIsEditRecipient(id);
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEmailValue('');
    if (setIsEditRecipient) {
      setIsEditRecipient(null);
    }
  };

  const handleSave = (id: number) => {
    const trimmed = emailValue.trim();
    const emailPattern = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    if (trimmed !== '' && emailPattern.test(trimmed)) {
      onUpdateEmail(id, trimmed);
      setEditingId(null);
      setEmailValue('');
      if (setIsEditRecipient) {
        setIsEditRecipient(null);
      }
    } else {
      showErrorSnackbar('Please enter a valid email address', 'right', 'bottom');
    }
  };

  return (
    <TableContainer
      component={Paper}
      sx={{
        mx: 'auto',
        boxShadow: 0,
        background: 'transparent',
        borderRadius: 0,
        maxHeight: 'calc(90vh - 190px)',
      }}
    >
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                color: 'white',
                fontWeight: 'bold',
                padding: '8px 16px',
                position: 'sticky',
                top: 0,
                backgroundColor: '#1976d2',
              }}
            >
              Sl No
            </TableCell>
            {addVoucherCol && (
              <TableCell
                sx={{
                  color: 'white',
                  fontWeight: 'bold',
                  padding: '8px 16px',
                  position: 'sticky',
                  top: 0,
                  backgroundColor: '#1976d2',
                }}
              >
                Voucher No#
              </TableCell>
            )}
            <TableCell
              sx={{
                color: 'white',
                fontWeight: 'bold',
                padding: '8px 16px',
                position: 'sticky',
                top: 0,
                backgroundColor: '#1976d2',
              }}
            >
              Recipient Name
            </TableCell>
            <TableCell
              sx={{
                color: 'white',
                fontWeight: 'bold',
                padding: '8px 16px',
                position: 'sticky',
                top: 0,
                backgroundColor: '#1976d2',
              }}
            >
              Recipient Email
            </TableCell>
            <TableCell
              sx={{
                color: 'white',
                fontWeight: 'bold',
                padding: '8px 16px',
                position: 'sticky',
                top: 0,
                backgroundColor: '#1976d2',
              }}
            >
              Status
            </TableCell>
            <TableCell
              sx={{
                color: 'white',
                fontWeight: 'bold',
                padding: '8px 16px',
                position: 'sticky',
                top: 0,
                backgroundColor: '#1976d2',
              }}
            >
              Action
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {recipients.map((ele, idx) => {
            const isFirstForThisCustomer = firstIndexOfId(ele.recipientId) === idx;
            const isInactive = ele.isActive !== 1;
            const canRemoveRecipient = recipients.length > 1;

            const row = (
              <TableRow
                key={ele.id}
                className="noTableCellBorder"
                sx={{
                  '&:nth-of-type(odd)': { backgroundColor: 'action.hover' },
                  height: 40,
                  '& td': { padding: '4px 16px' },
                  ...(isInactive && {
                    color: '#808080',
                    '& td': { color: '#808080' },
                    '&:hover': { cursor: 'not-allowed' },
                  }),
                }}
              >
                <TableCell>{idx + 1}</TableCell>
                {addVoucherCol && <TableCell>{ele.invoiceNumber}</TableCell>}

                <TableCell
                  sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {ele.name || '--'}
                </TableCell>

                <TableCell
                  sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    color: ele.email ? 'inherit' : 'error.main',
                    minWidth: 200,
                  }}
                >
                  {editingId === ele.recipientId && isFirstForThisCustomer && !isInactive ? (
                    <TextField
                      size="small"
                      value={emailValue}
                      autoFocus
                      onChange={(e) => setEmailValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSave(ele.recipientId);
                        if (e.key === 'Escape') handleCancel();
                      }}
                      sx={{
                        width: '100%',
                        '& .MuiOutlinedInput-root': {
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          '&:hover': {
                            border: '1px solid #1976d2',
                          },
                          '&.Mui-focused': {
                            border: '2px solid #1976d2',
                            boxShadow: '0 0 0 1px #1976d2',
                          },
                        },
                        '& .MuiInputBase-input': {
                          padding: '8px 12px',
                        },
                      }}
                    />
                  ) : (
                    ele.email || 'No email'
                  )}
                </TableCell>

                <TableCell>{isInactive ? 'In-active' : 'Active'}</TableCell>

                <TableCell sx={actionCellSx}>
                  {isFirstForThisCustomer && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {isInactive ? (
                        <>
                          <Tooltip title="Inactive recipient" arrow placement="top">
                            <span>
                              <IconButton size="small" disabled sx={disabledActionBtnSx}>
                                <DoDisturbAlt />
                              </IconButton>
                            </span>
                          </Tooltip>
                          {canRemoveRecipient && (
                            <Tooltip title="Remove recipient" arrow placement="top">
                              <IconButton size="small" color="error" onClick={() => removeRecipient(ele.id)} sx={actionBtnSx}>
                                <Close />
                              </IconButton>
                            </Tooltip>
                          )}
                        </>
                      ) : editingId === ele.recipientId ? (
                        <>
                          <Tooltip title="Save changes" arrow placement="top">
                            <IconButton color="success" size="small" onClick={() => handleSave(ele.recipientId)} sx={actionBtnSx}>
                              <Check />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Cancel" arrow placement="top">
                            <IconButton color="error" size="small" onClick={handleCancel} sx={actionBtnSx}>
                              <Close />
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : (
                        <>
                          <Tooltip title="Edit email" arrow placement="top">
                            <IconButton size="small" onClick={() => handleEdit(ele.recipientId, ele.email || '')} sx={actionBtnSx}>
                              <Edit />
                            </IconButton>
                          </Tooltip>
                          {canRemoveRecipient && (
                            <Tooltip title="Remove recipient" arrow placement="top">
                              <IconButton size="small" color="error" onClick={() => removeRecipient(ele.id)} sx={actionBtnSx}>
                                <Close />
                              </IconButton>
                            </Tooltip>
                          )}
                        </>
                      )}
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            );

            return row;
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
